import { useRef } from 'react'

import { ChevronDownIcon, XIcon } from 'lucide-react'

import { TokenDisplay, Tooltip } from '~/components/component-library'
import { TokenSelectorPopover } from '~/components/component-library/TokenSelector/TokenSelectorPopover'
import { BalancesProps } from '~/components/component-library/TokenSelector/types'
import {
  SelectedToken,
  useSelectedAggregatedBalance,
} from '~/components/component-library/TokenSelector/useSelectedAggregatedBalance'
import { IAddress } from '~/types'
import { cn } from '~/utils/tailwind-utils'

type TokenSelectorTriggerOnlyBaseProps = {
  token: SelectedToken
  onTokenChange: (token: SelectedToken) => void
  placeholder?: string
  filterAccountAddress?: IAddress
  allowRelayNetworks?: boolean
  tokenFilter?: (token: { address: string; chainId: number }) => boolean
  strictTokenFilter?: boolean
  chainFilter?: (chainId: number) => boolean
  /** Offer every smart-account chain, not just the org's active ones */
  includeAllSmartAccountChains?: boolean
  disabled?: boolean
  disabledReason?: string
  /** Tokens to always show first, regardless of balance or frequency */
  promotedTokens?: { address: string; chainId: number }[]
}

type TokenSelectorTriggerOnlyProps = TokenSelectorTriggerOnlyBaseProps &
  BalancesProps

export function TokenSelectorTriggerOnly({
  token,
  onTokenChange,
  placeholder = 'Select a token',
  filterAccountAddress,
  showBalances,
  allowRelayNetworks,
  tokenFilter,
  strictTokenFilter,
  chainFilter,
  includeAllSmartAccountChains,
  disabled,
  disabledReason,
  promotedTokens,
  sortBy,
}: TokenSelectorTriggerOnlyProps) {
  const triggerRef = useRef<HTMLButtonElement>(null)

  const { isExternalChain } = useSelectedAggregatedBalance({
    token,
    filterAccountAddress,
  })

  const triggerButton = (
    <button
      ref={triggerRef}
      type="button"
      disabled={disabled}
      className={cn(
        'flex w-full items-center gap-3 rounded-lg bg-white px-3.5 py-2.5 outline-none ring-1 ring-inset ring-gray-200 hover:bg-gray-50 focus-visible:ring-2 focus-visible:ring-blue-500 dark:bg-gray-900 dark:ring-gray-700 dark:hover:bg-gray-800',
        disabled && 'cursor-not-allowed opacity-50',
      )}
    >
      {token ? (
        <>
          <TokenDisplay
            address={token.address as `0x${string}`}
            chainId={token.chainId}
            layout="standard"
            size="base"
            hideInactiveChainIndicator={isExternalChain}
          />
          {!disabled && (
            <span
              role="button"
              aria-label="Clear token selection"
              tabIndex={-1}
              className="group flex aspect-square h-full items-center justify-center"
              onMouseDown={(e) => {
                e.preventDefault()
                e.stopPropagation()
              }}
              onClick={(e) => {
                e.stopPropagation()
                onTokenChange(null)
              }}
            >
              <span className="flex aspect-square h-auto items-center justify-center rounded-full p-1 text-primary group-hover:bg-gray-200 dark:group-hover:bg-gray-700">
                <XIcon className="size-4" />
              </span>
            </span>
          )}
        </>
      ) : (
        <>
          <span className="text-placeholder">{placeholder}</span>
          <ChevronDownIcon className="ml-auto size-4 text-placeholder" />
        </>
      )}
    </button>
  )

  if (disabled) {
    return disabledReason ? (
      <Tooltip content={disabledReason}>{triggerButton}</Tooltip>
    ) : (
      triggerButton
    )
  }

  return (
    <TokenSelectorPopover
      onChange={onTokenChange}
      filterAccountAddress={filterAccountAddress}
      showBalances={showBalances}
      allowRelayNetworks={allowRelayNetworks}
      tokenFilter={tokenFilter}
      strictTokenFilter={strictTokenFilter}
      chainFilter={chainFilter}
      includeAllSmartAccountChains={includeAllSmartAccountChains}
      promotedTokens={promotedTokens}
      sortBy={sortBy}
      restoreTriggerFocusOnClose
      triggerRef={triggerRef}
    >
      {triggerButton}
    </TokenSelectorPopover>
  )
}
